import React, { useState, useEffect, useCallback } from 'react';

const MarketingOps = () => {
  const [overview, setOverview] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [contentPipeline, setContentPipeline] = useState(null);
  const [contentPerf, setContentPerf] = useState(null);
  const [sources, setSources] = useState(null);
  const [cycleTime, setCycleTime] = useState(null);
  const [dailyMetrics, setDailyMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeframe, setTimeframe] = useState('week');
  const [activeTab, setActiveTab] = useState('overview');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    const tf = timeframe === 'week' ? '7' : timeframe === 'month' ? '30' : '365';
    try {
      const [ov, fn, tl, cp, cpr, src, ct, dm] = await Promise.all([
        fetch(`/api/marketing/overview?timeframe=${timeframe}`, { credentials: 'include' }).then(r => r.json()),
        fetch('/api/marketing/outreach-funnel', { credentials: 'include' }).then(r => r.json()),
        fetch(`/api/marketing/outreach-timeline?days=${tf}`, { credentials: 'include' }).then(r => r.json()),
        fetch('/api/marketing/content-pipeline', { credentials: 'include' }).then(r => r.json()),
        fetch('/api/marketing/content-performance', { credentials: 'include' }).then(r => r.json()),
        fetch('/api/marketing/leads-by-source', { credentials: 'include' }).then(r => r.json()),
        fetch('/api/marketing/cycle-time', { credentials: 'include' }).then(r => r.json()),
        fetch(`/api/marketing/daily-metrics?days=${tf}`, { credentials: 'include' }).then(r => r.json()),
      ]);
      setOverview(ov);
      setFunnel(fn);
      setTimeline(tl.timeline || []);
      setContentPipeline(cp);
      setContentPerf(cpr);
      setSources(src);
      setCycleTime(ct);
      setDailyMetrics(dm.metrics || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [timeframe]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const fmtCost = (v) => v ? `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '$0';
  const fmtNum = (v) => v ? Number(v).toLocaleString() : '0';
  const fmtPct = (v) => v ? `${v}%` : '0%';

  const maxSends = Math.max(...timeline.map(d => d.sends || 0), 1);
  const maxFunnel = funnel?.stages?.length > 0 ? funnel.stages[0].count : 1;

  const tabs = [
    { key: 'overview', label: '📊 Overview' },
    { key: 'outreach', label: '✉️ Outreach' },
    { key: 'content', label: '📱 Content' },
    { key: 'pipeline', label: '🔄 Pipeline' },
  ];

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h1 style={{ margin: 0 }}>📊 Marketing Operations</h1>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button className={`btn btn-sm ${timeframe === 'week' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTimeframe('week')}>This Week</button>
          <button className={`btn btn-sm ${timeframe === 'month' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTimeframe('month')}>This Month</button>
          <button className={`btn btn-sm ${timeframe === 'year' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTimeframe('year')}>All Time</button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '16px', borderBottom: '2px solid #e5e7eb', paddingBottom: '0' }}>
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            style={{
              padding: '8px 16px', border: 'none', background: 'none', cursor: 'pointer',
              fontSize: '14px', fontWeight: activeTab === t.key ? 600 : 400,
              color: activeTab === t.key ? '#3b82f6' : '#6b7280',
              borderBottom: activeTab === t.key ? '3px solid #3b82f6' : '3px solid transparent',
              marginBottom: '-2px',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {loading && <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>Loading marketing data...</div>}

      {!loading && (
        <>
          {/* ─── OVERVIEW TAB ─── */}
          {activeTab === 'overview' && (
            <>
              {/* Stat cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                <StatCard label="Emails Sent" value={fmtNum(overview?.emails_sent)} sub={`${timeframe}`} color="#3b82f6" />
                <StatCard label="Replies" value={fmtNum(overview?.replies_received)} sub={`${fmtPct(overview?.reply_rate)} reply rate`} color="#10b981" />
                <StatCard label="Posts Published" value={fmtNum(overview?.posts_published)} sub={`${timeframe}`} color="#8b5cf6" />
                <StatCard label="Pipeline Deals" value={fmtNum(overview?.pipeline_deals)} sub={fmtCost(overview?.pipeline_weighted_value) + ' weighted'} color="#f59e0b" />
                <StatCard label="Emailable Contacts" value={fmtNum(overview?.emailable_contacts)} sub={`${overview?.bounces || 0} bounces`} color="#ec4899" />
              </div>

              {/* Funnel */}
              <div className="card" style={{ marginBottom: '20px', padding: '20px' }}>
                <h3 style={{ marginTop: 0 }}>Outreach Funnel</h3>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  {funnel?.stages?.map((stage, i) => {
                    const widthPct = (stage.count / maxFunnel) * 100;
                    return (
                      <div key={i} style={{ textAlign: 'center', minWidth: '120px', flex: 1 }}>
                        <div style={{ fontSize: '24px', fontWeight: 700 }}>{fmtNum(stage.count)}</div>
                        <div style={{
                          height: `${Math.max(widthPct, 5)}px`,
                          background: stage.color,
                          borderRadius: '6px 6px 0 0',
                          marginTop: '8px',
                          opacity: 0.85,
                          transition: 'height 0.3s',
                        }} />
                        <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '6px' }}>{stage.label}</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Daily metrics chart */}
              <div className="card" style={{ padding: '20px' }}>
                <h3 style={{ marginTop: 0 }}>Daily Activity (Sends vs Replies)</h3>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: '120px', overflowX: 'auto', paddingBottom: '4px' }}>
                  {dailyMetrics.map((d, i) => (
                    <div key={i} style={{ minWidth: '24px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}>
                      <div title={`${d.date}\nSends: ${d.emails_sent}\nReplies: ${d.emails_replied}`} style={{ width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', height: '100%' }}>
                        <div style={{ height: `${(d.emails_replied / maxSends) * 100}px`, background: '#10b981', borderRadius: '2px 2px 0 0', minHeight: d.emails_replied > 0 ? '3px' : '0' }} />
                        <div style={{ height: `${(d.emails_sent / maxSends) * 100}px`, background: '#3b82f6', borderRadius: '2px 2px 0 0', minHeight: d.emails_sent > 0 ? '3px' : '0' }} />
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: '16px', marginTop: '8px', fontSize: '12px', color: '#6b7280' }}>
                  <span><span style={{ display: 'inline-block', width: '10px', height: '10px', background: '#3b82f6', borderRadius: '2px', marginRight: '4px' }} />Sends</span>
                  <span><span style={{ display: 'inline-block', width: '10px', height: '10px', background: '#10b981', borderRadius: '2px', marginRight: '4px' }} />Replies</span>
                </div>
              </div>
            </>
          )}

          {/* ─── OUTREACH TAB ─── */}
          {activeTab === 'outreach' && (
            <>
              {/* Timeline table */}
              <div className="card" style={{ overflow: 'hidden', marginBottom: '20px' }}>
                <div className="card-header" style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb' }}>
                  <h3 style={{ margin: 0 }}>Outreach Timeline</h3>
                </div>
                <table className="table" style={{ width: '100%' }}>
                  <thead>
                    <tr><th>Date</th><th>Emails Sent</th><th>Replies</th><th>Reply Rate</th><th>New Leads</th></tr>
                  </thead>
                  <tbody>
                    {dailyMetrics.slice().reverse().map((d, i) => (
                      <tr key={i}>
                        <td>{d.date}</td>
                        <td>{d.emails_sent}</td>
                        <td>{d.emails_replied}</td>
                        <td style={{ color: Number(d.reply_rate) > 5 ? '#10b981' : '#6b7280', fontWeight: Number(d.reply_rate) > 5 ? 600 : 400 }}>
                          {d.reply_rate}%
                        </td>
                        <td>{d.new_leads || 0}</td>
                      </tr>
                    ))}
                    {dailyMetrics.length === 0 && (
                      <tr><td colSpan="5" style={{ textAlign: 'center', padding: '20px', color: '#9ca3af' }}>No data</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Lead sources */}
              {sources?.sources?.length > 0 && (
                <div className="card" style={{ overflow: 'hidden' }}>
                  <div className="card-header" style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb' }}>
                    <h3 style={{ margin: 0 }}>Leads by Source</h3>
                  </div>
                  <table className="table" style={{ width: '100%' }}>
                    <thead>
                      <tr><th>Source</th><th>Deals</th><th>Unique Contacts</th><th>Weighted Value</th></tr>
                    </thead>
                    <tbody>
                      {sources.sources.map((s, i) => (
                        <tr key={i}>
                          <td><strong>{s.source}</strong></td>
                          <td>{s.deal_count}</td>
                          <td>{s.unique_contacts}</td>
                          <td style={{ color: '#059669' }}>{fmtCost(s.weighted_value)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {/* ─── CONTENT TAB ─── */}
          {activeTab === 'content' && (
            <>
              {/* Status breakdown */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', marginBottom: '20px' }}>
                {contentPipeline?.byStatus?.map((s, i) => (
                  <div key={i} className="card" style={{ padding: '14px', textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 700 }}>{s.count}</div>
                    <div style={{ fontSize: '12px', color: '#6b7280', textTransform: 'capitalize' }}>{s.status}</div>
                  </div>
                ))}
              </div>

              {/* Recent posts */}
              <div className="card" style={{ overflow: 'hidden' }}>
                <div className="card-header" style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb' }}>
                  <h3 style={{ margin: 0 }}>Recent Content</h3>
                </div>
                <table className="table" style={{ width: '100%' }}>
                  <thead>
                    <tr><th>Platform</th><th>Preview</th><th>Status</th><th>Model</th><th>Posted</th></tr>
                  </thead>
                  <tbody>
                    {contentPipeline?.recent?.map((p, i) => (
                      <tr key={i}>
                        <td><span style={{ textTransform: 'capitalize' }}>{p.platform}</span></td>
                        <td style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.preview}</td>
                        <td>
                          <span style={{
                            fontSize: '11px', padding: '2px 8px', borderRadius: '10px',
                            background: p.status === 'posted' ? '#dcfce7' : p.status === 'draft' ? '#fef3c7' : p.status === 'rejected' ? '#fee2e2' : '#f3f4f6',
                            color: p.status === 'posted' ? '#166534' : p.status === 'draft' ? '#92400e' : p.status === 'rejected' ? '#991b1b' : '#374151',
                          }}>{p.status}</span>
                        </td>
                        <td style={{ fontSize: '12px', color: '#6b7280' }}>{p.aiModel || '—'}</td>
                        <td style={{ fontSize: '12px', color: '#6b7280' }}>{p.postedAt ? new Date(p.postedAt).toLocaleDateString() : '—'}</td>
                      </tr>
                    ))}
                    {(!contentPipeline?.recent || contentPipeline.recent.length === 0) && (
                      <tr><td colSpan="5" style={{ textAlign: 'center', padding: '20px', color: '#9ca3af' }}>No content yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Content performance */}
              {contentPerf?.posts?.length > 0 && (
                <div className="card" style={{ overflow: 'hidden', marginTop: '20px' }}>
                  <div className="card-header" style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb' }}>
                    <h3 style={{ margin: 0 }}>Content Performance</h3>
                  </div>
                  <table className="table" style={{ width: '100%' }}>
                    <thead>
                      <tr><th>Platform</th><th>Preview</th><th>Impressions</th><th>Likes</th><th>Comments</th><th>Shares</th><th>Engagement</th></tr>
                    </thead>
                    <tbody>
                      {contentPerf.posts.map((p, i) => (
                        <tr key={i}>
                          <td style={{ textTransform: 'capitalize' }}>{p.platform}</td>
                          <td style={{ maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.preview}</td>
                          <td>{fmtNum(p.impressions)}</td>
                          <td>{p.likes}</td>
                          <td>{p.comments}</td>
                          <td>{p.shares}</td>
                          <td style={{ color: '#059669' }}>{p.engagement_rate ? `${p.engagement_rate}%` : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {/* ─── PIPELINE TAB ─── */}
          {activeTab === 'pipeline' && (
            <div className="card" style={{ overflow: 'hidden' }}>
              <div className="card-header" style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb' }}>
                <h3 style={{ margin: 0 }}>Pipeline Cycle Time</h3>
                {cycleTime?.source === 'estimated_from_deals' && (
                  <span style={{ fontSize: '12px', color: '#9ca3af' }}> (estimated from deal age)</span>
                )}
              </div>
              <table className="table" style={{ width: '100%' }}>
                <thead>
                  <tr><th>Stage</th><th>Avg Time (hours)</th><th>Avg Time (days)</th><th>Deals</th></tr>
                </thead>
                <tbody>
                  {cycleTime?.stages?.map((s, i) => (
                    <tr key={i}>
                      <td><strong style={{ textTransform: 'capitalize' }}>{s.new_stage?.replace(/_/g, ' ')}</strong></td>
                      <td>{fmtNum(s.avg_hours)}</td>
                      <td>{s.avg_hours ? (s.avg_hours / 24).toFixed(1) : '0'} days</td>
                      <td>{s.transitions}</td>
                    </tr>
                  ))}
                  {(!cycleTime?.stages || cycleTime.stages.length === 0) && (
                    <tr><td colSpan="4" style={{ textAlign: 'center', padding: '20px', color: '#9ca3af' }}>No cycle time data</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const StatCard = ({ label, value, sub, color }) => (
  <div className="card" style={{ padding: '16px', borderLeft: `4px solid ${color}` }}>
    <div style={{ fontSize: '12px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
    <div style={{ fontSize: '28px', fontWeight: 700, margin: '4px 0' }}>{value}</div>
    <div style={{ fontSize: '12px', color: '#9ca3af' }}>{sub}</div>
  </div>
);

export default MarketingOps;
